/* eslint-disable react/jsx-no-comment-textnodes */
import Footer from '@react/shared/components/Footer';
import Image from '@react/shared/components/Image';
import { RESOURCE_CENTER_PATH } from '@sly/core/constants/dashboardAppPaths';
import cx from 'classnames';
import dynamic from 'next/dynamic';
import React, { useEffect } from 'react';
import { assetPath } from 'sly/lib/path';
import Header from 'sly/resourceCenter/components/Header';
import HelmetComponent from 'sly/resourceCenter/components/Helmet';
import ResourceCenterChatBotWrapper from 'sly/resourceCenter/components/ResourceCenterChatBotWrapper';
import { usePrefetch } from 'sly/services/api/prefetch';
import { pageNames, useEvents } from 'sly/services/events';
import Heading from 'sly/system/Heading';
import Link from 'sly/system/Link';

import CareScoutBanner from '../../../CareScoutBanner';

const ArticlesListByResourceCenter = dynamic(
  /* #__LOADABLE__ */ () =>
    import(
      /* webpackChunkName: "chunkArticlesListByTopic" */ 'sly/resourceCenter/components/ArticlesListByResourceCenter'
    )
);
const ArticlesList = dynamic(
  /* #__LOADABLE__ */ () =>
    import(
      /* webpackChunkName: "chunkArticlesList" */ 'sly/resourceCenter/components/ArticlesList'
    )
);
const SubscribeEmail = dynamic(
  /* #__LOADABLE__ */ () =>
    import(
      /* webpackChunkName: "chunkSubscribeEmail" */ 'sly/resourceCenter/components/SubscribeEmail'
    )
);

const cleanObject = (obj) =>
  // eslint-disable-next-line no-return-assign
  Object.entries(obj).reduce((a, [k, v]) => (v ? ((a[k] = v), a) : a), {});

const HomePage = () => {
  const { requestInfo } = usePrefetch('getResourceCenterMainInfo');

  const { events } = useEvents();
  useEffect(() => {
    events.page(pageNames.ResourceCenterHome, {});
  }, []);

  const { featuredArticles, recentArticles, mainTopics, topicRes } =
    requestInfo?.result || {};

  const articlesBox = {
    Featured_Articles: featuredArticles?.length > 0 ? featuredArticles : null,
    Recent_Articles: recentArticles?.length > 0 ? recentArticles : null,
  };

  if (!requestInfo.hasFinished) {
    return (
      <div
        className='flex h-[100vh] items-center justify-center'
        data-tp-id='gen-Flex-91bb07e2-3ca2-4a3d-81b2-4c8efcf03ec3'
      >
        <Image
          src={assetPath('uploads/react-assets/homebase/loader.svg')}
          data-tp-id='gen-Image-dd3c8abc-05db-4278-88ca-bfbd79fcfc7d'
          layout='fill'
          alt='loader'
        />
      </div>
    );
  }

  return (
    <ResourceCenterChatBotWrapper>
      <HelmetComponent
        path={RESOURCE_CENTER_PATH}
        title='Retirement Home Senior Living Resources & FAQs | Seniorly'
        shortDescription='What to know when looking for a nursing home or retirement community. Visit Seniorly’s
education center for answers to questions about senior living.'
      />

      <CareScoutBanner />

      <Header />

      <div
        className={cx(
          'mb-xxl bg-harvest-lighter-90 py-xxl px-m',
          'md:px-[calc((100%_-_42.5rem)_/_2)] md:py-xxxl',
          'lg:px-[calc((100%_-_64.5rem)_/_2)]'
        )}
        data-tp-id='gen-Block-4206695e-12cb-4fcc-acd9-20ef932dc815'
      >
        <Heading
          className='font-t-xxl mb-l'
          font='title-xxlarge'
          data-tp-id='gen-Heading-b584b8c5-fbd0-4c46-a1e2-bdad6493e602'
          as='h1'
        >
          Seniorly Resource Center
        </Heading>

        <div
          className='font-b-l max-w-[42.5rem]'
          data-tp-id='gen-Block-5c1bfe6c-4873-45a6-9421-642480b34d29'
        >
          Learn how to navigate senior living, from what to look for in a
          community, how to finance, move in tips and more.
        </div>
      </div>

      {Object.values(cleanObject(articlesBox)) &&
        Object.entries(articlesBox)?.map(([key, value]) => (
          <div
            className='mb-xxl'
            key={key}
            data-tp-id='gen-Block-4a626b93-fb29-4f6e-a0bc-cf221bb304c7'
          >
            <ArticlesList
              articlesTitle={key.replace(/_/g, ' ')}
              articles={value}
            />
          </div>
        ))}

      {mainTopics
        ?.filter(({ slug }) => slug)
        .map(({ slug, name, id }) => (
          <div
            className='mb-xxl'
            key={id}
            data-tp-id='gen-Block-c9037e18-8e8c-4559-a186-334ce3d03701'
          >
            <ArticlesListByResourceCenter
              limit={3}
              topic={slug}
              id={id}
              articlesTitle={name}
              withRedirectToTopicPage
            />
          </div>
        ))}

      <div
        className='font-t-l mx-m mb-l md:mx-auto md:w-[42.5rem] lg:w-[64.5rem]'
        data-tp-id='gen-Block-b35b0f43-f34d-47f6-8fec-de12aa5f3f8f'
      >
        Explore Topics
      </div>

      <div
        className={cx(
          'mx-auto mb-xxl grid grid-cols-[minmax(auto,_20.5rem)] justify-center px-m',
          'gap-m',
          'md:grid-cols-[repeat(2,_20.5rem)]',
          'md:grid-rows-[repeat(3,_17rem)] md:gap-l',
          'md:mb-xxxl md:px-0',
          'lg:grid-cols-[repeat(2,_31.5rem)]'
        )}
        data-tp-id='gen-Grid-1be26a89-7c5d-4527-bd8f-9ed29162633f'
      >
        {topicRes?.map(({ slug, name, description, img }) => (
          <Link
            className={cx('relative', 'resource-center-styled-link')}
            key={slug}
            to={`${RESOURCE_CENTER_PATH}/${slug}`}
            data-tp-id='gen-Link-7900c1b1-5955-40ac-a464-75ed213fb4cc'
          >
            <div
              className='aspect-[7/6] overflow-hidden md:aspect-[6/5] lg:aspect-[16/9]'
              data-tp-id='gen-Block-f77c1053-2f18-4621-a88d-59e8379ba044'
            >
              <Image
                path={img?.path}
                alt={`${name} image`}
                sources={[328, 504]}
                sizes='(max-width: 1079px) 328px, 504px'
                data-tp-id='gen-Image-fb71dc9f-4b25-4fd7-ab40-ddd2ecf5c39e'
                objectFit='cover'
              />
              <div
                className={cx(
                  'rounded-box absolute top-0 left-0 overflow-hidden',
                  'resource-center-explore-topic-bg-img z-[1]',
                  'flex h-full w-full flex-col justify-end p-l'
                )}
                data-tp-id='gen-div-cd472e36-bee5-4a3d-92de-5a7eb57cfafb'
              >
                <div
                  className='font-t-m mb-s text-white'
                  data-tp-id='gen-Block-228025a4-3b5c-4697-8f8d-98dba75885b0'
                >
                  {name}
                </div>
                <div
                  className='font-b-m text-white'
                  data-tp-id='gen-Block-a4ee4428-9158-4c94-a864-2f5c3f5b7c49'
                >
                  {description}
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <SubscribeEmail />

      <Footer />
    </ResourceCenterChatBotWrapper>
  );
};

HomePage.displayName = 'ResourceCenterHomePage';

export default HomePage;
